import { useEffect } from 'react';
import {
  useFetchOptionDataByTypes,
  useGetOptionDataByType,
  useIsOptionDataLoading,
} from '@/stores';
import { OptionResponse } from '@shared/types';
import { MasterCategoryType } from '@shared/constants';
import { IndividualOptionDataType } from '@/stores/optionData/types';

// Providers
import { useFirebase } from '@/providers/firebase';

interface UseOptionDataOptions {
  types: MasterCategoryType[];
  force?: boolean;
}

interface UseOptionDataReturn {
  data: Record<IndividualOptionDataType, OptionResponse[]>;
  isLoading: boolean;
  refetch: (forceRefresh?: boolean) => Promise<void>;
}

/**
 * Advanced hook for fetching option data by types with caching and loading states
 * Now supports grouped types like MasterCategoryType.SKILLS, MasterCategoryType.CERTIFICATIONS
 */
export const useOptionData = ({
  types,
  force = false,
}: UseOptionDataOptions): UseOptionDataReturn => {
  const fetchOptionDataByTypes = useFetchOptionDataByTypes();
  const getOptionDataByType = useGetOptionDataByType();
  const isLoading = useIsOptionDataLoading();
  const { getToken } = useFirebase();

  // Auto fetch when component mounts
  const typesKey = types?.join(',');
  useEffect(() => {
    // Fetch option data by types

    const fetchOptionData = async () => {
      const token = await getToken();

      await fetchOptionDataByTypes(types, force, token);
    };

    if (types.length > 0) {
      fetchOptionData();
    }

    // Don't need to put "getToken" in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesKey, force, fetchOptionDataByTypes]); // Dependency array

  // Prepare return data - each type returns combined data for grouped types
  const data =
    types.reduce(
      (acc, type) => ({
        ...acc,
        ...getOptionDataByType(type),
      }),
      {} as Record<IndividualOptionDataType, OptionResponse[]>,
    ) || ({} as Record<IndividualOptionDataType, OptionResponse[]>);

  const refetch = async (forceRefresh = false) => {
    await fetchOptionDataByTypes(types, forceRefresh);
  };

  return {
    data,
    refetch,
    isLoading,
  };
};
